import React, { useEffect, useState } from 'react';
import api, { getMerchants, updateMerchant, getCategories, deleteMerchant, renameMerchant, mergeMerchant } from '../api';

/**
 * Page Marchands
 * Attribution catégorie + sous-catégorie
 * → recatégorise automatiquement toutes les transactions
 */
export default function Merchants({ refreshKey, onUpdated }) {

  const [merchants, setMerchants] = useState([]);
  const [categories, setCategories] = useState([]);

  const [filterName, setFilterName] = useState('');
  const [filterCatId, setFilterCatId] = useState('');
  const [filterSubcatId, setFilterSubcatId] = useState('');

  // État modale fusion
  const [mergeModal, setMergeModal] = useState(null); // { source: merchant }
  const [mergeTargetId, setMergeTargetId] = useState('');
  const [mergeSearch, setMergeSearch] = useState('');
  const [mergeLoading, setMergeLoading] = useState(false);

  // 🔄 Charger marchands
  const fetchMerchants = async () => {
    try {
      const res = await getMerchants();
      setMerchants(res.data);
    } catch (err) {
      console.error('Erreur fetch merchants:', err);
    }
  };

  // 🔄 Charger catégories + sous-catégories
  const fetchCategories = async () => {
    try {
      const res = await getCategories();
      setCategories(res.data);
    } catch (err) {
      console.error('Erreur fetch categories:', err);
    }
  };

  // chargement initial + refresh global
  useEffect(() => {
    fetchMerchants();
    fetchCategories();
  }, [refreshKey]);

  // 🔎 retourne les sous-catégories d'une catégorie
  const getSubcategories = (categoryId) => {
    const cat = categories.find(c => c.id === Number(categoryId));
    return cat ? cat.subcategories : [];
  };

  /**
   * 🔄 Update merchant + refresh global app
   */
  const handleChange = async (merchantId, categoryId, subcategoryId) => {
    try {
      await updateMerchant(merchantId, {
        category_id: categoryId || null,
        subcategory_id: subcategoryId || null
      });
      fetchMerchants();
      if (onUpdated) onUpdated();
    } catch (err) {
      console.error('Erreur update merchant:', err);
    }
  };

  /**
   * 🗑️ Suppression merchant
   */
  const handleDeleteMerchant = async (id) => {
    if (!window.confirm("Supprimer ce marchand ?")) return;
    try {
      await deleteMerchant(id);
      fetchMerchants();
      if (onUpdated) onUpdated();
    } catch (err) {
      console.error("Erreur suppression merchant :", err);
    }
  };

  /**
   * ✏️ Renommage merchant
   */
  const handleRenameMerchant = async (id, currentName) => {
    const newName = prompt("Nouveau nom du marchand :", currentName);
    if (!newName) return;
    try {
      await renameMerchant(id, { name: newName });
      fetchMerchants();
      onUpdated?.();
    } catch (err) {
      console.error("Erreur rename merchant:", err);
    }
  };

  /**
   * 🔀 Ouvrir la modale de fusion
   */
  const handleOpenMerge = (merchant) => {
    setMergeModal({ source: merchant });
    setMergeTargetId('');
    setMergeSearch('');
  };

  /**
   * 🔀 Confirmer la fusion
   */
  const handleMerge = async () => {
    if (!mergeTargetId || !mergeModal) return;
    setMergeLoading(true);
    try {
      await mergeMerchant(mergeModal.source.id, { targetId: parseInt(mergeTargetId) });
      setMergeModal(null);
      fetchMerchants();
      onUpdated?.();
    } catch (err) {
      console.error("Erreur fusion merchant:", err);
      alert("Erreur lors de la fusion.");
    } finally {
      setMergeLoading(false);
    }
  };

  // Sous-catégories disponibles pour le filtre
  const filterSubcats = filterCatId
    ? (categories.find(c => c.id === Number(filterCatId))?.subcategories || [])
    : categories.flatMap(c => c.subcategories);

  const isUncategorized = (m) => !m.category_id || !m.subcategory_id;
  const uncategorizedCount = merchants.filter(isUncategorized).length;

  // Liste affichée dans le tableau selon les filtres actifs
  const displayedMerchants = merchants.filter(m => {
    if (filterName && !m.name.toLowerCase().includes(filterName.toLowerCase())) return false;
    if (filterCatId === '__none__' && !isUncategorized(m)) return false;
    if (filterCatId && filterCatId !== '__none__' && String(m.category_id) !== filterCatId) return false;
    if (filterSubcatId && String(m.subcategory_id) !== filterSubcatId) return false;
    return true;
  });

  const hasFilter = filterName || filterCatId || filterSubcatId;

  // Liste filtrée pour la modale (exclut la source)
  const filteredMerchants = merchants.filter(m =>
    mergeModal && m.id !== mergeModal.source.id &&
    m.name.toLowerCase().includes(mergeSearch.toLowerCase())
  );

  return (
    <div style={{ marginTop: 40 }}>
      <h2>Marchands</h2>

      {/* FILTRES */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
        {uncategorizedCount > 0 && (
          <button
            onClick={() => { setFilterCatId(filterCatId === '__none__' ? '' : '__none__'); setFilterSubcatId(''); }}
            style={{
              padding: '6px 12px', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 600,
              border: `1px solid ${filterCatId === '__none__' ? 'var(--danger)' : '#f5c2c2'}`,
              background: filterCatId === '__none__' ? 'var(--danger)' : '#fff5f5',
              color: filterCatId === '__none__' ? '#fff' : 'var(--danger)',
            }}
          >
            ⚠ Non catégorisés — {uncategorizedCount}
          </button>
        )}
        <input
          type="text"
          placeholder="Rechercher un marchand…"
          value={filterName}
          onChange={e => setFilterName(e.target.value)}
          style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 13, minWidth: 200 }}
        />
        <select
          value={filterCatId}
          onChange={e => { setFilterCatId(e.target.value); setFilterSubcatId(''); }}
          style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 13 }}
        >
          <option value="">Toutes les catégories</option>
          <option value="__none__">— Non catégorisés</option>
          {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select
          value={filterSubcatId}
          onChange={e => setFilterSubcatId(e.target.value)}
          disabled={filterSubcats.length === 0}
          style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 13 }}
        >
          <option value="">Toutes les sous-catégories</option>
          {filterSubcats.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        {hasFilter && (
          <button
            onClick={() => { setFilterName(''); setFilterCatId(''); setFilterSubcatId(''); }}
            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--surface)', fontSize: 13, cursor: 'pointer', color: 'var(--text-soft)' }}
          >
            Réinitialiser
          </button>
        )}
        <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 'auto' }}>
          {displayedMerchants.length} / {merchants.length} marchands
        </span>
      </div>

      <table
        border="1"
        cellPadding="8"
        style={{ borderCollapse: 'collapse', width: '100%' }}
      >
        <thead>
          <tr>
            <th>Marchand</th>
            <th>Catégorie</th>
            <th>Sous-catégorie</th>
            <th title="Multi-catégorie : toujours envoyer à la file de catégorisation" style={{ textAlign: 'center', fontSize: 11, color: 'var(--text-muted)' }}>Multi-cat.</th>
            <th></th>
          </tr>
        </thead>

        <tbody>
          {displayedMerchants.length === 0 ? (
            <tr>
              <td colSpan="4" style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 20 }}>
                {merchants.length === 0 ? 'Aucun marchand détecté' : 'Aucun marchand ne correspond aux filtres'}
              </td>
            </tr>
          ) : (
            displayedMerchants.map(m => (
              <tr key={m.id} style={isUncategorized(m) ? { background: '#fff8f8' } : undefined}>
                <td>
                  {m.name}{" "}
                  <button
                    title="Renommer"
                    onClick={() => handleRenameMerchant(m.id, m.name)}
                  >✏️</button>{" "}
                  <button
                    title="Fusionner dans un autre marchand"
                    onClick={() => handleOpenMerge(m)}
                  >🔀</button>
                </td>

                {/* Catégorie */}
                <td>
                  <select
                    value={m.category_id || ''}
                    onChange={(e) =>
                      handleChange(m.id, e.target.value, null)
                    }
                  >
                    <option value="">Non catégorisé</option>
                    {categories.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </td>

                {/* Sous-catégorie dépendante */}
                <td>
                  <select
                    value={m.subcategory_id || ''}
                    disabled={!m.category_id}
                    onChange={(e) =>
                      handleChange(m.id, m.category_id, e.target.value)
                    }
                  >
                    <option value="">—</option>
                    {getSubcategories(m.category_id).map(sc => (
                      <option key={sc.id} value={sc.id}>
                        {sc.name}
                      </option>
                    ))}
                  </select>
                </td>

                {/* Multi-catégorie */}
                <td style={{ textAlign: 'center' }}>
                  <div
                    title="Marchand multi-catégorie : toujours envoyer à la file de catégorisation"
                    onClick={async () => {
                      const newVal = !m.multi_category;
                      setMerchants(prev => prev.map(x => x.id === m.id ? { ...x, multi_category: newVal } : x));
                      await api.patch(`/merchants/${m.id}/multi-category`, { multi_category: newVal });
                    }}
                    style={{
                      width: 36, height: 20, borderRadius: 10, cursor: 'pointer', margin: '0 auto',
                      background: m.multi_category ? 'var(--primary)' : 'var(--border)',
                      position: 'relative', transition: 'background .2s',
                    }}
                  >
                    <div style={{
                      position: 'absolute', top: 2, left: m.multi_category ? 18 : 2,
                      width: 16, height: 16, borderRadius: '50%', background: '#fff',
                      transition: 'left .2s', boxShadow: '0 1px 3px rgba(0,0,0,.2)',
                    }} />
                  </div>
                </td>

                {/* 🗑️ Supprimer */}
                <td>
                  <button onClick={() => handleDeleteMerchant(m.id)}>
                    🗑️
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {/* ===== MODALE FUSION ===== */}
      {mergeModal && (
        <div style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 10,
            padding: 28,
            minWidth: 380,
            maxWidth: 480,
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          }}>
            <h3 style={{ marginTop: 0 }}>🔀 Fusionner un marchand</h3>

            <p style={{ marginBottom: 6 }}>
              Absorber <strong>« {mergeModal.source.name} »</strong> dans :
            </p>

            {/* Recherche */}
            <input
              type="text"
              placeholder="Rechercher un marchand…"
              value={mergeSearch}
              onChange={e => { setMergeSearch(e.target.value); setMergeTargetId(''); }}
              style={{
                width: '100%', padding: '8px 10px',
                border: '1px solid #ccc', borderRadius: 6,
                marginBottom: 8, boxSizing: 'border-box', fontSize: 14,
              }}
              autoFocus
            />

            {/* Liste */}
            <div style={{
              maxHeight: 220, overflowY: 'auto',
              border: '1px solid #e0e0e0', borderRadius: 6,
              marginBottom: 16,
            }}>
              {filteredMerchants.length === 0 ? (
                <div style={{ padding: '12px 14px', color: '#888', fontSize: 14 }}>
                  Aucun résultat
                </div>
              ) : (
                filteredMerchants.map(m => (
                  <div
                    key={m.id}
                    onClick={() => setMergeTargetId(String(m.id))}
                    style={{
                      padding: '10px 14px',
                      cursor: 'pointer',
                      background: mergeTargetId === String(m.id) ? '#e8f0fe' : 'transparent',
                      borderBottom: '1px solid #f0f0f0',
                      fontSize: 14,
                      fontWeight: mergeTargetId === String(m.id) ? 600 : 400,
                    }}
                  >
                    {m.name}
                    {m.category_name && (
                      <span style={{ color: '#888', marginLeft: 8, fontWeight: 400 }}>
                        ({m.category_name})
                      </span>
                    )}
                  </div>
                ))
              )}
            </div>

            {/* Confirmation visuelle */}
            {mergeTargetId && (
              <p style={{
                background: '#fff3cd', border: '1px solid #ffc107',
                borderRadius: 6, padding: '8px 12px',
                fontSize: 13, marginBottom: 16,
              }}>
                ⚠️ <strong>« {mergeModal.source.name} »</strong> sera supprimé.
                Toutes ses transactions seront rattachées à{' '}
                <strong>« {merchants.find(m => String(m.id) === mergeTargetId)?.name} »</strong>.
                Cette action est irréversible.
              </p>
            )}

            {/* Boutons */}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setMergeModal(null)}
                style={{
                  padding: '8px 18px', borderRadius: 6,
                  border: '1px solid #ccc', background: '#f5f5f5',
                  cursor: 'pointer', fontSize: 14,
                }}
              >
                Annuler
              </button>
              <button
                onClick={handleMerge}
                disabled={!mergeTargetId || mergeLoading}
                style={{
                  padding: '8px 18px', borderRadius: 6,
                  border: 'none',
                  background: mergeTargetId ? '#d32f2f' : '#ccc',
                  color: '#fff', cursor: mergeTargetId ? 'pointer' : 'default',
                  fontSize: 14, fontWeight: 600,
                }}
              >
                {mergeLoading ? 'Fusion…' : 'Fusionner'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}